<!-- ❗Errors in the form are set on line 60 -->
<script setup lang="ts">
import { VForm } from 'vuetify/components/VForm'
import { useGenerateImageVariant } from '@core/composable/useGenerateImageVariant'
import authV2LoginIllustrationBorderedDark from '@images/pages/auth-v2-login-illustration-bordered-dark.png'
import authV2LoginIllustrationBorderedLight from '@images/pages/auth-v2-login-illustration-bordered-light.png'
import authV2LoginIllustrationDark from '@images/pages/auth-v2-login-illustration-dark.png'
import authV2LoginIllustrationLight from '@images/pages/auth-v2-login-illustration-light.png'
import authV2MaskDark from '@images/pages/misc-mask-dark.png'
import authV2MaskLight from '@images/pages/misc-mask-light.png'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import { emailValidator, requiredValidator } from '@core/utils/validators'
import { useFeatureStore } from '@/stores/features'
import { useLanguageStore } from '@/stores/language'
import { getI18n } from '@/plugins/i18n'
import { usePlatformBranding } from '@/composables/usePlatformBranding'
import I18n from '@core/components/I18n.vue'

const authThemeImg = useGenerateImageVariant(authV2LoginIllustrationLight, authV2LoginIllustrationDark, authV2LoginIllustrationBorderedLight, authV2LoginIllustrationBorderedDark, true)

const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark)

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const isPasswordVisible = ref(false)

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const ability = useAbility()

const errors = ref<Record<string, string | undefined>>({
  email: undefined,
  password: undefined,
  general: undefined,
})
const infoMessage = ref('')
// Atterrato qui dopo aver cliccato "Verifica email": nessun altro punto della UI
// confermava che la verifica fosse andata a buon fine, sembrava che il pulsante
// "non funzionasse" anche se il backend aveva gia' segnato l'email come
// verificata (bug reale segnalato dall'utente, 2026-09-22).
const emailJustVerified = ref(route.query.verified === '1')
const resendVerificationLoading = ref(false)
const platformRequiresEmailVerification = ref(false)
const lastLoginRequiresVerification = ref(false)
const { brandName, logoUrl, authHeadline, authSubheadline, isWhitelabel, loadPlatformBranding, platformStatus, showPlatformLogoFallback } = usePlatformBranding()
const authHeadlineText = computed(() => authHeadline.value || t('auth.welcome_title', { app: brandName.value }))
const authSubheadlineText = computed(() => authSubheadline.value || t('auth.welcome_subtitle'))

const refVForm = ref<VForm>()

const credentials = ref({
  email: '',
  password: '',
})

const rememberMe = ref(false)

const canShowResendVerification = computed(() => {
  return platformRequiresEmailVerification.value
    && !!credentials.value.email
    && (lastLoginRequiresVerification.value || !!errors.value.email)
})

const loadPlatformStatus = async () => {
  try {
    await loadPlatformBranding()
    platformRequiresEmailVerification.value = !!platformStatus.value.require_email_verification
  }
  catch {}
}

const resendEmailVerification = async () => {
  if (!credentials.value.email || resendVerificationLoading.value)
    return

  resendVerificationLoading.value = true
  infoMessage.value = ''

  try {
    // Il messaggio del backend e' sempre in italiano (AuthController::
    // resendEmailVerification(), generico e senza dati dinamici) - usiamo
    // sempre la traduzione lato frontend, mai il testo grezzo della risposta.
    await $api('/auth/email/resend', {
      method: 'POST',
      body: {
        email: credentials.value.email,
      },
    })

    infoMessage.value = t('auth.verification_email_resent')
  }
  catch {
    infoMessage.value = t('auth.verification_email_resend_error')
  }
  finally {
    resendVerificationLoading.value = false
  }
}

const login = async () => {
  try {
    errors.value = {
      email: undefined,
      password: undefined,
      general: undefined,
    }
    infoMessage.value = ''
    lastLoginRequiresVerification.value = false

    // Initialize CSRF cookie
    await $api('/sanctum/csrf-cookie')

    const res = await $api('/auth/login', {
      method: 'POST',
      body: {
        email: credentials.value.email,
        password: credentials.value.password,
      },
      onResponseError({ response }) {
        const payload = response._data || {}
        const responseErrors = payload.errors || {}
        lastLoginRequiresVerification.value = !!payload.requires_email_verification

        // Il backend non conosce la lingua del pannello e restituisce questo
        // messaggio sempre in italiano (AuthController::login()) - usiamo la
        // stringa tradotta lato frontend invece del testo grezzo del backend.
        const emailVerificationMessage = lastLoginRequiresVerification.value
          ? t('auth.email_verification_required')
          : undefined

        errors.value = {
          email: emailVerificationMessage
            || (Array.isArray(responseErrors.email) ? responseErrors.email[0] : undefined),
          password: Array.isArray(responseErrors.password) ? responseErrors.password[0] : undefined,
          general: emailVerificationMessage
            || (Array.isArray(responseErrors.general)
              ? responseErrors.general[0]
              : (payload.message || payload.error || undefined)),
        }
      },
    })

    const { accessToken, userData, userAbilityRules } = res

    // Salva tenantId se presente (importante per le API successive)
    const tid = userData?.tenant?.id
    if (tid) {
      try {
        localStorage.setItem('tenantId', String(tid))

        const allowedApps = Array.isArray(userData?.allowed_apps) ? userData.allowed_apps : []
        const defaultApp = allowedApps.find((app: any) => Number(app.id) === Number(userData?.default_app_id))
          || (allowedApps.length === 1 ? allowedApps[0] : null)

        if (defaultApp?.id) {
          localStorage.setItem('activeAppId', String(defaultApp.id))
          localStorage.setItem('activeAppName', defaultApp.name || '')
        } else {
          localStorage.removeItem('activeAppId')
          localStorage.removeItem('activeAppName')
        }
      }
      catch (e) {
        console.error('Failed to save tenantId', e)
      }
    }

    useCookie('userAbilityRules').value = userAbilityRules
    ability.update(userAbilityRules)

    setUserDataCookie(userData)
    useCookie('accessToken').value = accessToken

    // Scaldiamo la cache di moduli/traduzioni prima del redirect, ma senza
    // bloccarlo: a questo punto il login e' gia' andato a buon fine (cookie
    // gia' salvati sopra). Un tenant/owner che non ha ancora creato nessuna
    // app fa fallire /features con 400 ("App context not resolved") - prima
    // di questo fix l'eccezione risaliva fino al catch esterno della
    // funzione e interrompeva silenziosamente login() PRIMA del redirect:
    // il pulsante "Accedi" sembrava non fare nulla, ma l'utente era gia'
    // autenticato (un refresh della pagina lo mostrava loggato). Bug reale
    // segnalato dall'utente il 2026-09-23.
    try {
      const featureStore = useFeatureStore()
      const languageStore = useLanguageStore()

      await featureStore.load()
      await languageStore.fetchTranslations(getI18n().global.locale.value)
    }
    catch (bootstrapError) {
      console.error('Post-login bootstrap (features/translations) failed, redirecting anyway', bootstrapError)
    }

    // Redirect based on role
    const userRole = userData.role
    if (userRole === 'admin') {
      await nextTick(() => {
        router.replace({ name: 'admin-dashboard' })
      })
    }
    else if (userRole === 'client') {
      await nextTick(() => {
        router.replace({ name: 'portfolio' })
      })
    }
    else if (userRole === 'cashier') {
      await nextTick(() => {
        router.replace({ name: 'apps-loyalty-card-pos-cashier' })
      })
    }
    else {
      await nextTick(() => {
        router.replace(route.query.to ? String(route.query.to) : '/')
      })
    }
  }
  catch (err) {
    console.error(err)
  }
}

onMounted(() => {
  loadPlatformStatus()
})

const onSubmit = () => {
  refVForm.value?.validate()
    .then(({ valid: isValid }) => {
      if (isValid)
        login()
    })
}
</script>

<template>
  <div class="auth-logo d-flex align-center gap-x-3">
    <Component
      :is="isWhitelabel ? 'div' : 'RouterLink'"
      :to="isWhitelabel ? undefined : '/'"
      class="d-flex align-center gap-x-3"
    >
      <img
        v-if="logoUrl"
        :src="logoUrl"
        :alt="brandName"
        style="display:block; max-width: 180px; max-height: 48px; width: auto; height: auto; object-fit: contain;"
      >
      <VNodeRenderer v-else-if="showPlatformLogoFallback" :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ brandName }}
      </h1>
    </Component>
  </div>

  <div class="auth-lang-selector">
    <I18n
      :languages="themeConfig.app.i18n.langConfig"
      location="bottom end"
    />
  </div>

  <VRow
    no-gutters
    class="auth-wrapper bg-surface"
  >
    <VCol
      md="8"
      class="d-none d-md-flex"
    >
      <div class="position-relative bg-background w-100 me-0">
        <div
          class="d-flex align-center justify-center w-100 h-100"
          style="padding-inline: 6.25rem;"
        >
          <VImg
            max-width="613"
            :src="authThemeImg"
            class="auth-illustration mt-16 mb-2"
          />
        </div>

        <img
          class="auth-footer-mask"
          :src="authThemeMask"
          alt="auth-footer-mask"
          height="280"
          width="100"
        >
      </div>
    </VCol>

    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-4"
      >
        <VCardText>
          <h4 class="text-h4 mb-1">
            {{ authHeadlineText }}
          </h4>
          <p class="mb-0">
            {{ authSubheadlineText }}
          </p>
        </VCardText>

        <VCardText v-if="emailJustVerified">
          <VAlert
            type="success"
            variant="tonal"
            closable
            @click:close="emailJustVerified = false"
          >
            {{ $t('auth.email_verified_success') }}
          </VAlert>
        </VCardText>

        <VCardText>
          <VForm
            ref="refVForm"
            @submit.prevent="onSubmit"
          >
            <VRow>
              <!-- email -->
              <VCol cols="12">
                <AppTextField
                  v-model="credentials.email"
                  :label="$t('auth.email')"
                  type="email"
                  autofocus
                  :rules="[requiredValidator, emailValidator]"
                  :error-messages="errors.email"
                />
              </VCol>

              <!-- password -->
              <VCol cols="12">
                <AppTextField
                  v-model="credentials.password"
                  :label="$t('auth.password')"
                  :rules="[requiredValidator]"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  autocomplete="password"
                  :error-messages="errors.password"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />

                <div class="d-flex align-center flex-wrap justify-space-between my-6">
                  <VCheckbox
                    v-model="rememberMe"
                    :label="$t('auth.remember_me')"
                  />
                  <RouterLink
                    class="text-primary ms-2 mb-1"
                    to="/forgot-password"
                  >
                    {{ $t('auth.forgot_password') }}
                  </RouterLink>
                </div>

                <VBtn
                  block
                  type="submit"
                >
                  {{ $t('auth.login') }}
                </VBtn>
                <div
                  v-if="errors.general"
                  class="mt-2 text-error"
                >
                  {{ errors.general }}
                </div>
                <div
                  v-if="canShowResendVerification"
                  class="mt-3"
                >
                  <VBtn
                    variant="text"
                    color="primary"
                    :loading="resendVerificationLoading"
                    @click="resendEmailVerification"
                  >
                    {{ $t('auth.resend_verification_email') }}
                  </VBtn>
                </div>
                <div
                  v-if="infoMessage"
                  class="mt-2 text-info"
                >
                  {{ infoMessage }}
                </div>
              </VCol>

              <!-- create account: nascosto su dominio whitelabel, la self-registration
                   creerebbe un nuovo Tenant indipendente su AppForge stessa
                   invece che un operatore del tenant whitelabel - bloccato
                   anche lato route guard per l'accesso diretto all'URL -->
              <VCol
                v-if="!isWhitelabel"
                cols="12"
                class="text-center"
              >
                <span>{{ $t('auth.new_to_platform') }}</span>
                <RouterLink
                  class="text-primary ms-1"
                  :to="{ name: 'register' }"
                >
                  {{ $t('auth.create_account') }}
                </RouterLink>
              </VCol>
              
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core-scss/template/pages/page-auth";

.auth-lang-selector {
  position: absolute;
  inset-block-start: 2rem;
  inset-inline-end: 2.3rem;
  z-index: 2;
}
</style>
